import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Lock, PlayCircle } from "lucide-react";
import { YT_EMBED_PARAMS } from "./YouTubeGuidePlayer";
import { fmtIST, humanRemaining, localEquivalent } from "@/lib/ist";

const ytId = (url) => url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|live\/|shorts\/)|youtu\.be\/)([\w-]{6,})/)?.[1];
const driveId = (url) => (url.match(/drive\.google\.com\/file\/d\/([\w-]+)/) || url.match(/drive\.google\.com\/(?:open|uc)\?(?:.*&)?id=([\w-]+)/))?.[1];

const Shell = ({ children, overlay }) => (
  <div className="relative w-full aspect-video rounded-2xl border border-border bg-black overflow-hidden" onContextMenu={(e) => e.preventDefault()}>
    {children}
    {overlay}
  </div>
);

const mmss = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

const StartOverlay = ({ onStart, title, resumeAt = 0 }) => (
  <button type="button" data-testid="strict-start-btn" data-resume={resumeAt > 0 ? Math.floor(resumeAt) : undefined} onClick={onStart} className="absolute inset-0 grid place-items-center bg-[#1A2E26]/80 text-white">
    <span className="flex flex-col items-center gap-3">
      <span className="grid place-items-center w-20 h-20 rounded-full bg-white/15 border border-white/40 backdrop-blur"><PlayCircle size={44} /></span>
      <span className="font-serif text-xl">{resumeAt > 0 ? `Resume ${title} from ${mmss(resumeAt)}` : `Play ${title}`}</span>
      <span className="text-xs text-white/70">{resumeAt > 0 ? "Continues where you left off · " : "Plays once, from start to end · "}no pause, rewind or fast-forward</span>
    </span>
  </button>
);

const DoneOverlay = () => (
  <div data-testid="strict-done-overlay" className="absolute inset-0 grid place-items-center bg-[#1A2E26]/90 text-white">
    <span className="flex flex-col items-center gap-2"><CheckCircle2 size={40} className="text-emerald-300" /><span className="font-serif text-xl">Session completed</span><span className="text-xs text-white/70">This video cannot be played again.</span></span>
  </div>
);

export const WatchedOverlay = () => (
  <div data-testid="strict-watched" className="w-full aspect-video rounded-2xl border border-border bg-[#1A2E26] text-white grid place-items-center">
    <span className="flex flex-col items-center gap-2"><Lock size={36} className="text-white/70" /><span className="font-serif text-xl">Already watched</span><span className="text-xs text-white/70">Each session video can be played only once.</span></span>
  </div>
);

const Html5Strict = ({ url, title, onEnded, onProgress, resumeAt = 0 }) => {
  const ref = useRef(null);
  const last = useRef(resumeAt);
  const [phase, setPhase] = useState("idle");
  const start = () => { setPhase("playing"); const v = ref.current; if (v && resumeAt > 0) { try { v.currentTime = resumeAt; } catch { /* set after metadata */ } } v?.play().catch(() => setPhase("idle")); };
  const onMeta = () => { const v = ref.current; if (v && resumeAt > 0 && v.currentTime < resumeAt - 1) v.currentTime = resumeAt; };
  const onTime = () => { if (ref.current) { last.current = Math.max(last.current, ref.current.currentTime); onProgress?.(ref.current.currentTime, ref.current.duration); } };
  const onSeeking = () => { const v = ref.current; if (v && Math.abs(v.currentTime - last.current) > 1) v.currentTime = last.current; };
  const onPause = () => { const v = ref.current; if (v && phase === "playing" && !v.ended) v.play().catch(() => {}); };
  const ended = () => { setPhase("done"); onProgress?.(ref.current?.duration || last.current, ref.current?.duration || last.current, true); onEnded?.(); };
  return (
    <Shell overlay={phase === "idle" ? <StartOverlay onStart={start} title={title} resumeAt={resumeAt} /> : phase === "done" ? <DoneOverlay /> : null}>
      <video ref={ref} data-testid="classroom-player" src={url} playsInline controls={false} controlsList="nodownload noplaybackrate noremoteplayback" disablePictureInPicture onContextMenu={(e) => e.preventDefault()}
        onLoadedMetadata={onMeta} onTimeUpdate={onTime} onSeeking={onSeeking} onPause={onPause} onEnded={ended} className="w-full h-full pointer-events-none" />
    </Shell>
  );
};

const YouTubeStrict = ({ id, title, onEnded, onProgress, resumeAt = 0 }) => {
  const ref = useRef(null);
  const dur = useRef(0);
  const last = useRef(resumeAt);
  const [phase, setPhase] = useState("idle");
  useEffect(() => {
    const onMsg = (e) => {
      if (!e.origin.includes("youtube.com")) return;
      try {
        const d = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        if (d?.event === "infoDelivery" && d.info) {
          if (d.info.duration) dur.current = d.info.duration;
          if (d.info.currentTime != null) {
            if (d.info.currentTime < last.current - 2) post("seekTo", [last.current, true]);
            last.current = Math.max(last.current, d.info.currentTime);
            onProgress?.(d.info.currentTime, dur.current);
          }
        }
        if (d?.event === "onStateChange" && d.info === 2 && phase === "playing") post("playVideo");
        if (d?.event === "onStateChange" && d.info === 0) { setPhase("done"); onProgress?.(dur.current, dur.current, true); onEnded?.(); }
      } catch { /* ignore */ }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  });
  const post = (func, args = []) => ref.current?.contentWindow?.postMessage(JSON.stringify({ event: "command", func, args }), "*");
  const hideCaptions = () => { post("unloadModule", ["captions"]); post("unloadModule", ["cc"]); };
  const start = () => { setPhase("playing"); ref.current?.contentWindow?.postMessage(JSON.stringify({ event: "listening", id: 1 }), "*"); hideCaptions(); if (resumeAt > 0) post("seekTo", [resumeAt, true]); post("playVideo"); };
  return (
    <Shell overlay={phase === "idle" ? <StartOverlay onStart={start} title={title} resumeAt={resumeAt} /> : phase === "done" ? <DoneOverlay /> : <div className="absolute inset-0" />}>
      <iframe ref={ref} data-testid="classroom-player" title={title} src={`https://www.youtube.com/embed/${id}?${YT_EMBED_PARAMS}&origin=${encodeURIComponent(window.location.origin)}`} className="absolute left-0 w-full h-[calc(100%+140px)] -top-[70px] pointer-events-none" allow="autoplay; encrypted-media" />
    </Shell>
  );
};

const DriveStrict = ({ id, title, onEnded, onProgress }) => {
  const [phase, setPhase] = useState("idle");
  const start = () => { setPhase("playing"); onProgress?.(0, 0, true); onEnded?.(); };
  return (
    <Shell overlay={phase === "idle" ? <StartOverlay onStart={start} title={title} /> : <><div data-testid="player-top-guard" className="absolute inset-x-0 top-0 h-16 z-10" /><div data-testid="player-corner-guard" className="absolute right-0 bottom-0 w-24 h-14 z-10" /></>}>
      <iframe data-testid="classroom-player" title={title} src={`https://drive.google.com/file/d/${id}/preview`} className="absolute left-0 w-full h-[calc(100%+56px)] -top-[56px]" allow="autoplay" />
    </Shell>
  );
};

export const StrictPlayer = ({ url, title, onEnded, onProgress, resumeAt: resumeRaw = 0 }) => {
  const resumeAt = resumeRaw >= 3 ? resumeRaw : 0;
  const yt = ytId(url);
  if (yt) return <YouTubeStrict id={yt} title={title} onEnded={onEnded} onProgress={onProgress} resumeAt={resumeAt} />;
  const gd = driveId(url);
  if (gd) return <DriveStrict id={gd} title={title} onEnded={onEnded} onProgress={onProgress} />;
  return <Html5Strict url={url} title={title} onEnded={onEnded} onProgress={onProgress} resumeAt={resumeAt} />;
};

export const useCountdown = (targetIso, serverNowIso) => {
  const offset = useRef(serverNowIso ? new Date(serverNowIso).getTime() - Date.now() : 0);
  const [ms, setMs] = useState(() => (targetIso ? new Date(targetIso).getTime() - (Date.now() + offset.current) : 0));
  useEffect(() => {
    if (!targetIso) return undefined;
    const tick = () => setMs(new Date(targetIso).getTime() - (Date.now() + offset.current));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [targetIso]);
  return ms;
};

const pad = (n) => String(Math.max(0, Math.floor(n))).padStart(2, "0");

export const LockedPlayer = ({ targetIso, serverNow, title, onReached }) => {
  const ms = useCountdown(targetIso, serverNow);
  const reached = useRef(false);
  useEffect(() => { if (ms <= 0 && !reached.current && targetIso) { reached.current = true; onReached?.(); } }, [ms, targetIso, onReached]);
  const s = ms / 1000;
  const cells = ms > 24 * 3600 * 1000 ? [["Days", pad(s / 86400)], ["Hours", pad((s % 86400) / 3600)], ["Minutes", pad((s % 3600) / 60)], ["Seconds", pad(s % 60)]] : [["Hours", pad(s / 3600)], ["Minutes", pad((s % 3600) / 60)], ["Seconds", pad(s % 60)]];
  const local = localEquivalent(targetIso);
  return (
    <div data-testid="locked-player" className="relative w-full aspect-video rounded-2xl border border-border bg-[#1A2E26] text-white overflow-hidden grid place-items-center px-6 text-center">
      <div>
        <span className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-white/10 border border-white/30"><Lock size={28} /></span>
        <p className="mt-4 font-serif text-xl sm:text-2xl" data-testid="locked-player-title">{title}</p>
        <p className="mt-1 text-[10px] uppercase tracking-[0.3em] text-white/60">Video starts in</p>
        {ms > 0 ? (
          <>
            <div className="mt-3 flex flex-wrap justify-center items-end gap-2 sm:gap-3 font-serif tabular-nums" data-testid="locked-player-clock">
              {cells.map(([l, v]) => (
                <div key={l} className="text-center"><div className="text-3xl sm:text-5xl bg-white/10 border border-white/20 rounded-2xl px-3 sm:px-4 py-2 min-w-[3.5rem] sm:min-w-[4.5rem]">{v}</div><div className="mt-1 text-[10px] uppercase tracking-widest text-white/60">{l}</div></div>
              ))}
            </div>
            <p className="mt-3 text-sm text-white/85" data-testid="locked-player-human">{humanRemaining(ms)} to go</p>
          </>
        ) : <p className="mt-3 font-serif text-2xl text-emerald-300">Opening now…</p>}
        <p className="mt-2 text-xs text-white/60" data-testid="locked-player-ist">Starts {fmtIST(targetIso, true)}</p>
        {local && <p className="mt-0.5 text-xs text-white/50" data-testid="locked-player-local">That is {local}.</p>}
      </div>
    </div>
  );
};

export const Countdown = ({ targetIso, serverNow, label, onReached }) => {
  const ms = useCountdown(targetIso, serverNow);
  const reached = useRef(false);
  useEffect(() => { if (ms <= 0 && !reached.current && targetIso) { reached.current = true; onReached?.(); } }, [ms, targetIso, onReached]);
  if (!targetIso) return null;
  const s = ms / 1000;
  const cells = ms > 24 * 3600 * 1000 ? [["Days", pad(s / 86400)], ["Hours", pad((s % 86400) / 3600)], ["Minutes", pad((s % 3600) / 60)], ["Seconds", pad(s % 60)]] : [["Hours", pad(s / 3600)], ["Minutes", pad((s % 3600) / 60)], ["Seconds", pad(s % 60)]];
  const local = localEquivalent(targetIso);
  return (
    <div data-testid="classroom-countdown" className="mt-6 rounded-3xl border border-[#1A2E26]/15 bg-[#F4F1EA] px-6 py-6">
      <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{label}</p>
      {ms > 0 ? (
        <>
          <div className="mt-3 flex flex-wrap items-end gap-3 font-serif tabular-nums" data-testid="classroom-countdown-clock">
            {cells.map(([l, v]) => (
              <div key={l} className="text-center"><div className="text-4xl sm:text-5xl bg-white rounded-2xl border border-border px-4 py-2 min-w-[4.5rem]">{v}</div><div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div></div>
            ))}
          </div>
          <p className="mt-3 text-sm" data-testid="classroom-countdown-human">{humanRemaining(ms)} to go</p>
        </>
      ) : (
        <p className="mt-2 font-serif text-2xl" data-testid="classroom-countdown-date">Opening now…</p>
      )}
      <p className="mt-2 text-xs text-muted-foreground" data-testid="classroom-countdown-ist">Starts {fmtIST(targetIso, true)}. The time is fixed in IST for everyone.</p>
      {local && <p className="mt-1 text-xs text-muted-foreground" data-testid="classroom-countdown-local">That is {local}.</p>}
    </div>
  );
};
